import React, { useEffect, useState, useCallback } from 'react';
import { reportAPI } from '../lib/api';
import { BarChart3 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';

export default function ReportsPage() {
  const [summary, setSummary] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [budgetData, setBudgetData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [activeTab, setActiveTab] = useState('summary');

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, auditRes, budgetRes] = await Promise.all([
        reportAPI.summary({ startDate, endDate }),
        reportAPI.audit({ limit: 50 }),
        reportAPI.budget()
      ]);
      setSummary(summaryRes.data);
      setAuditLogs(auditRes.data.logs);
      setBudgetData(budgetRes.data);
    } catch (err) { toast.error('Failed to load reports'); }
    finally { setLoading(false); }
  }, [startDate, endDate]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const formatDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="space-y-4" data-testid="reports-page">
      <div className="flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-slate-700" />
        <h1 className="text-xl font-bold tracking-tight text-slate-900">Reports</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-md p-0.5 w-fit">
        {['summary', 'budget', 'audit'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-1 text-xs font-medium rounded ${activeTab === tab ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-${tab}`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {activeTab === 'summary' && (
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-end gap-3">
            <div>
              <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">From</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1 w-36" data-testid="report-start-date" />
            </div>
            <div>
              <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">To</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1 w-36" data-testid="report-end-date" />
            </div>
            <Button size="sm" onClick={fetchReports} className="bg-slate-900 hover:bg-slate-800 text-white text-xs" data-testid="generate-report-btn">
              Generate
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div></div>
          ) : summary && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <SummaryCard label="Total Amount" value={`₹${(summary.summary?.totalAmount || 0).toLocaleString('en-IN')}`} />
                <SummaryCard label="Total Vouchers" value={summary.summary?.totalVouchers || 0} />
                <SummaryCard label="Avg Amount" value={`₹${Math.round(summary.summary?.avgAmount || 0).toLocaleString('en-IN')}`} />
                <SummaryCard label="Max Amount" value={`₹${(summary.summary?.maxAmount || 0).toLocaleString('en-IN')}`} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <h3 className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-3">By Type</h3>
                  {summary.byType?.map((t, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                      <span className="text-xs text-slate-600">{t._id}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] text-slate-400">{t.count} vouchers</span>
                        <span className="text-xs font-medium text-slate-900">₹{t.total?.toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  ))}
                  {(!summary.byType || summary.byType.length === 0) && <p className="text-xs text-slate-400 text-center py-4">No data</p>}
                </div>

                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <h3 className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-3">By Status</h3>
                  {summary.byStatus?.map((s, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                      <span className="text-xs text-slate-600">{s._id}</span>
                      <span className="text-xs font-medium text-slate-900">{s.count}</span>
                    </div>
                  ))}
                  {(!summary.byStatus || summary.byStatus.length === 0) && <p className="text-xs text-slate-400 text-center py-4">No data</p>}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'budget' && (
        <div className="space-y-4">
          {budgetData && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <SummaryCard label="Total Budget" value={`₹${(budgetData.totalBudget || 0).toLocaleString('en-IN')}`} />
                <SummaryCard label="Total Spent" value={`₹${(budgetData.totalSpent || 0).toLocaleString('en-IN')}`} />
                <SummaryCard label="Over Budget" value={budgetData.overBudgetCount || 0} highlight={budgetData.overBudgetCount > 0} />
                <SummaryCard label="At Risk (80%+)" value={budgetData.atRiskCount || 0} highlight={budgetData.atRiskCount > 0} />
              </div>
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm" data-testid="budget-report-table">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Category</th>
                      <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Budget</th>
                      <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Spent</th>
                      <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Remaining</th>
                      <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase w-32">Usage</th>
                      <th className="text-center px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {budgetData.categories?.filter(c => c.budget > 0).map(c => (
                      <tr key={c.categoryId} className={`border-t ${c.alertLevel === 'exceeded' ? 'bg-red-50/50' : c.alertLevel === 'warning' ? 'bg-amber-50/50' : ''} hover:bg-slate-50`}>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-sm" style={{ background: c.color }} />
                            <span className="text-xs font-medium text-slate-900">{c.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-right text-slate-700">₹{c.budget.toLocaleString('en-IN')}</td>
                        <td className="px-4 py-2.5 text-xs text-right font-medium text-slate-900">₹{c.spent.toLocaleString('en-IN')}</td>
                        <td className="px-4 py-2.5 text-xs text-right text-slate-500">₹{c.remaining.toLocaleString('en-IN')}</td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${c.alertLevel === 'exceeded' ? 'bg-red-500' : c.alertLevel === 'warning' ? 'bg-amber-500' : 'bg-slate-900'}`}
                                style={{ width: `${Math.min(c.pct, 100)}%` }}
                              />
                            </div>
                            <span className="text-[10px] font-medium text-slate-600 w-8 text-right">{c.pct}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          {c.alertLevel === 'exceeded' && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700">OVER</span>}
                          {c.alertLevel === 'warning' && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">WARN</span>}
                          {c.alertLevel === 'normal' && <span className="text-[9px] px-1.5 py-0.5 rounded bg-green-50 text-green-700">OK</span>}
                        </td>
                      </tr>
                    ))}
                    {(!budgetData.categories || budgetData.categories.filter(c => c.budget > 0).length === 0) && (
                      <tr><td colSpan="6" className="text-center py-8 text-sm text-slate-400">No categories with budgets set</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'audit' && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-log-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Timestamp</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Entity</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Action</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase hidden sm:table-cell">Performed By</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map((log) => (
                  <tr key={log._id || `${log.entityId}-${log.timestamp}`} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2 text-xs text-slate-500">{formatDate(log.timestamp)}</td>
                    <td className="px-4 py-2 text-xs"><span className="font-medium text-slate-900">{log.entityType}</span> <span className="text-slate-400">{log.entityId}</span></td>
                    <td className="px-4 py-2 text-xs text-slate-700">{log.action}</td>
                    <td className="px-4 py-2 text-xs text-slate-500 hidden sm:table-cell">{log.performedByName}</td>
                  </tr>
                ))}
                {auditLogs.length === 0 && <tr><td colSpan="4" className="text-center py-8 text-sm text-slate-400">No audit logs yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, highlight }) {
  return (
    <div className={`bg-white border rounded-lg p-3 ${highlight ? 'border-red-300 bg-red-50/50' : 'border-slate-200'}`}>
      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
      <p className={`text-lg font-bold mt-1 ${highlight ? 'text-red-600' : 'text-slate-900'}`}>{value}</p>
    </div>
  );
}
